import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { Sun, Moon, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";
import { isMaintenanceMode } from "@/utils/maintenanceMode";

const APP_BASE_URL = process.env.REACT_APP_APP_URL || "";

export default function Header() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const maintenanceMode = isMaintenanceMode();

  const topLinks = [
    { to: "/#features-section", label: t("home.howItWorksNav") },
    ...(!maintenanceMode ? [{ to: "/pricing", label: t("nav.pricing") }] : []),
    { to: "/about", label: t("footer.about") },
  ];

  const isActive = (path) => {
    const basePath = (path || "").split("#")[0] || "/";
    return basePath === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(basePath);
  };

  return (
    <header
      className="relative z-50 h-[60px] border-b border-border bg-background/95 backdrop-blur-md"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-full flex items-center gap-6">
        <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
          <img
            src={resolvedTheme === "dark" ? logoDark : logoLight}
            alt="Veriksa"
            className="h-8 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1" data-testid="top-nav">
          {topLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                isActive(to)
                  ? "text-foreground bg-secondary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <button
          className="md:hidden ml-auto p-1.5 rounded-sm hover:bg-secondary/60 transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        <div className="hidden md:flex items-center gap-2 ml-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            className="rounded-sm h-7 w-7"
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </Button>
          {!maintenanceMode && (
            <>
              <a href={`${APP_BASE_URL}/login`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-sm font-medium h-7 px-3"
                  data-testid="header-login-btn"
                >
                  {t("nav.signIn")}
                </Button>
              </a>
              <a href={`${APP_BASE_URL}/register`}>
                <Button
                  size="sm"
                  className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-medium h-7 px-3"
                  data-testid="header-signup-btn"
                >
                  {t("nav.getStarted")}
                </Button>
              </a>
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden absolute top-[60px] left-0 right-0 border-b border-border bg-background shadow-lg z-40"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col p-3 gap-1">
            {topLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive(to)
                    ? "text-foreground bg-secondary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 px-4 pb-3 pt-1 border-t border-border">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="rounded-sm h-7 w-7"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </Button>
            {!maintenanceMode && (
              <div className="flex items-center gap-2 ml-auto">
                <a href={`${APP_BASE_URL}/login`}>
                  <Button variant="ghost" size="sm" className="rounded-sm text-sm h-7 px-3">
                    {t("nav.signIn")}
                  </Button>
                </a>
                <a href={`${APP_BASE_URL}/register`}>
                  <Button size="sm" className="rounded-sm bg-brand-primary text-white text-sm h-7 px-3">
                    {t("nav.getStarted")}
                  </Button>
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
